import React, { useEffect, useState, useCallback, useMemo } from "react";
import Layout, { Container } from "../components/layout";
import { IoAddOutline } from "react-icons/io5";
import axios from "axios";
import { apiurl } from "../config/config";
import { useUser } from "../config/userProvider";
import { Link, useNavigate } from "react-router-dom";
import { CircularLoader, SuwasthiLoader } from "../components/loader";
import BackHeader from "../components/backHeader";
import Pagination from "../components/pagination";
import Card from "../components/card";
import { toast } from "react-toastify";
import DataNotFound from "../components/dataNotFound";
import { Checkbox } from "../components/checkbox";
import ButtonContainer from "../components/buttonContainer";
import SearchContainer from "../components/searchContainer";
import useApiProgress from "../hooks/useApiProgress";
import LoginAccessComposer from "../components/loginAccessComposer.js";

function Teams() {
  const { userData } = useUser();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { progress, isLoading, callApi } = useApiProgress();

  const [deleteIds, setDeleteIds] = useState([]);

  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const [open, setOpen] = useState(false);

  console.log("deleteIds", deleteIds);

  /** -----------------------------
   *  Fetch Members
   * ------------------------------
   */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // const response = await axios.post(
      //   `${apiurl}/api/user/get?page=${currentPage}&limit=${itemsPerPage}`,
      //   {},
      //   { headers: { "Content-Type": "application/json" } }
      // );
      const response = await callApi({
        method: "POST",
        url: `${apiurl}/api/user/get?page=${currentPage}&limit=${itemsPerPage}`,
        headers: { "Content-Type": "application/json" },
        // data: {}
      });

      console.log("Fetched Users Response:", response);

      setUsers(response?.users || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** ----------------------------
   * Search Filter (memoized)
   * -----------------------------
   */
  const filteredUsers = useMemo(() => {
    if (!searchInput.trim()) return users;
    return users.filter((u) =>
      `${u.firstName} ${u.lastName} ${u.email} ${u.role}`
        .toLowerCase()
        .includes(searchInput.toLowerCase()),
    );
  }, [users, searchInput]);

  /** ----------------------------
   * Delete Selected Users
   * -----------------------------
   */
  const handleDeleteUsers = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.post(
        `${apiurl}/admin/v1/user/delete`,
        { userObjIds: deleteIds },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.data.success) {
        toast.success(response?.data?.message || "Users deleted!");
        setDeleteIds([]);
        fetchUsers();
      } else {
        toast.error("Failed to delete users");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error(error);
    } finally {
      setDeleteLoading(false);
      setConfirmDelete(false);
    }
  };

  /** ----------------------------
   * Checkbox Logic
   * -----------------------------
   */
  const handleSelectAll = () => {
    if (deleteIds.length === filteredUsers.length) setDeleteIds([]);
    else setDeleteIds(filteredUsers.map((u) => u._id));
  };

  const toggleSelection = (id) => {
    setDeleteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <Layout>
      {confirmDelete && (
        <Card.confirmation
          message="Are you sure you want to delete the selected members?"
          onConfirm={handleDeleteUsers}
          onClose={() => setConfirmDelete(false)}
          loading={deleteLoading}
          loadingTitle="Deleting..."
        />
      )}

      <Container overflow="overflow-hidden">
        {/* 
              {loading ? (
          <SuwasthiLoader maintext="Fetching Teams..." />
        ) : ( */}
        {isLoading ? (
          <CircularLoader progress={progress} />
        ) : (
          <div className="border bg-white h-full w-full px-2 py-2">
            <div className="relative flex flex-col gap-2 w-full rounded-lg px-2 h-full p-2">
              {/* Top Header */}
              <BackHeader
                title={
                  <span className="flex items-center gap-2">
                    Our Team
                    <span className="hidden lg:flex text-gray-500">
                      ({pagination?.totalItems || 0})
                    </span>
                    <select
                      value={itemsPerPage}
                      className="text-xs rounded text-black border px-2"
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[100, 250, 500, 1000].map((limit) => (
                        <option key={limit} value={limit}>
                          {limit}
                        </option>
                      ))}
                    </select>
                  </span>
                }
                rightSide={
                  <div className="flex gap-2 items-center">
                    {deleteIds.length > 0 &&
                      userData?.userType === "superadmin" && (
                        <button
                          onClick={() => setConfirmDelete(true)}
                          className="bg-red-50 text-xs border border-red-300 text-[#c00218] hover:bg-[#c00218] hover:text-white rounded-lg py-1.5 px-3"
                        >
                          Delete
                        </button>
                      )}

                    {userData?.userType !== "admin" && (
                      <Link to="/teams/create">
                        <ButtonContainer icon={<IoAddOutline />}>
                          Create
                        </ButtonContainer>
                      </Link>
                    )}

                    <div className="hidden md:flex">
                      <SearchContainer
                        value={searchInput}
                        placeholder="Search team..."
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>

                    <>
                      <button
                        onClick={() => setOpen(true)}
                        className="bg-blue-600 text-white px-6 py-2 rounded"
                      >
                        Update Login Access
                      </button>

                      {open && (
                        <LoginAccessComposer
                          onClose={() => setOpen(false)}
                          userObjIds={deleteIds}
                        />
                      )}
                    </>
                  </div>
                }
              />

              {/* Table */}
              <div className="relative overflow-y-auto h-full rounded-lg">
                {filteredUsers?.length > 0 ? (
                  <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 shadow-sm">
                      <tr>
                        <th className="p-4 w-4">
                          <Checkbox
                            checked={
                              filteredUsers.length > 0 &&
                              deleteIds.length === filteredUsers.length
                            }
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th className="px-6 py-3">Name</th>
                        <th className="px-6 py-3">Employee ID</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3">Email</th>
                        <th className="px-6 py-3">Phone</th>
                        <th className="px-6 py-3">Role</th>
                        <th className="px-6 py-3">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredUsers.map((user, index) => (
                        <tr
                          key={user._id}
                          className={`border-b cursor-pointer hover:bg-gray-50 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                        >
                          <td className="p-4">
                            <Checkbox
                              checked={deleteIds.includes(user._id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelection(user._id);
                              }}
                              disabled={user.userType === "superadmin"}
                            />
                          </td>

                          <td
                            className="px-6 py-4 font-medium text-gray-900 capitalize"
                            onClick={() => navigate(`/teams/${user._id}`)}
                          >
                            {user.firstName} {user.lastName}
                          </td>

                          <td
                            className="px-6 py-4"
                            onClick={() => navigate(`/teams/${user._id}`)}
                          >
                            SUW{user.employeeDetails?.employeeId}
                          </td>

                          <td className="px-6 py-4">{user.status}</td>
                          <td className="px-6 py-4">{user.email}</td>
                          <td className="px-6 py-4">{user.phone}</td>
                          <td className="px-6 py-4 capitalize">{user.role}</td>

                          <td className="px-6 py-4 flex flex-col gap-1">
                            <p
                              className="text-green-600 cursor-pointer"
                              onClick={() => navigate(`/teams/${user._id}`)}
                            >
                              View
                            </p>

                            <Link
                              to={`/teams/${user._id}/manage`}
                              className="text-blue-600"
                            >
                              Manage
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <DataNotFound.page label="Team" />
                )}
              </div>

              <Pagination
                currentPage={currentPage}
                totalItems={pagination?.totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
}

export default Teams;